import type { Viewport } from '@skill-trees/shared';
import {
    computeCenteredViewport,
    computeFitViewport,
    type WorldBounds,
} from './viewportCentering';

export const DEFAULT_VIEW_ANIMATION_MS = 500;

/** Options shared by every step that moves the viewport. */
type ViewportStepOptions = {
    durationMs?: number;
    /**
     * Compute the view area as if the skill panel were in this state instead of its
     * current one — 'collapsed' while it is mid-collapse, 'expanded' when a later step
     * will open it and the focus should land centered in the remaining space.
     */
    skillPanel?: 'collapsed' | 'expanded';
};

export type AnimationStep =
    | ({ kind: 'panToNode'; nodeId: string } & ViewportStepOptions)
    | ({ kind: 'zoomToNode'; nodeId: string; k: number } & ViewportStepOptions)
    | ({ kind: 'zoomToPoint'; x: number; y: number; k: number } & ViewportStepOptions)
    | ({ kind: 'zoomToFit'; paddingPx?: number } & ViewportStepOptions)
    | { kind: 'selectNode'; nodeId: string }
    | { kind: 'highlightNode'; nodeId: string; color: string }
    | { kind: 'openSkillPanel' }
    | { kind: 'closeSkillPanel' }
    | { kind: 'scrollGuideToSection'; sectionId: string }
    | { kind: 'wait'; ms: number };

export type AnimationScript = readonly AnimationStep[];

/**
 * How a run() ended. 'superseded' scripts were replaced by a newer run()/cancel(),
 * whose initiator already settled the app state; 'failed' scripts died on their own
 * (interrupted animation, unknown node, degenerate view area), so their owner must
 * still settle whatever state the dropped steps were going to apply.
 */
export type ScriptOutcome = 'completed' | 'superseded' | 'failed';

/**
 * Actuators and sensors the robot drives. Implemented by the view layer
 * (TreeView wires it to React Flow and the interactors); sensors must report
 * live values since scripts read them mid-sequence, after waits and animations.
 */
export interface AnimationStage {
    /**
     * Animate the viewport. Resolves true on completion, false if the animation could
     * not run or was interrupted (a user gesture, or a newer animation superseding it),
     * so the script it gates can end instead of parking forever.
     */
    animateViewport(viewport: Viewport, durationMs: number): Promise<boolean>;
    selectNode(nodeId: string): void;
    /** Pulse a node in the given color; resolves when the pulse animation finishes. */
    highlightNode(nodeId: string, color: string): Promise<boolean>;
    setSkillPanelOpen(open: boolean): void;
    /** Scroll the open skill guide to a section (heading id); no-op when it cannot. */
    scrollGuideToSection(sectionId: string): void;
    /** World-space center of a node, or null when unknown or unpositioned. */
    getNodeCenter(nodeId: string): { x: number; y: number } | null;
    /** World-space bounding box of the whole tree (node extents included), or null when empty. */
    getTreeBounds(): WorldBounds | null;
    /** Live viewport zoom (not app state, which can lag behind mid-animation). */
    getCurrentZoom(): number;
    getZoomLimits(): { min: number; max: number };
    /** Screen area the tree can occupy (window minus chrome and skill panel). */
    getViewArea(options?: { skillPanel?: 'collapsed' | 'expanded' }): {
        availableWidth: number;
        windowHeight: number;
    };
}

/**
 * Executes view-animation scripts step by step against an AnimationStage.
 * One script runs at a time: run() cancels the script already in progress,
 * so stale sequences never fire their remaining steps.
 */
export class AnimationRobot {
    /** Bumped by cancel(); an in-flight script stops once its token goes stale. */
    private generation = 0;
    private cancelPendingWait: (() => void) | null = null;

    constructor(private readonly stage: AnimationStage) {}

    /**
     * Run a script, canceling any script already in progress. Remaining steps are
     * dropped as soon as a newer run()/cancel() takes over ('superseded') or a step
     * could not execute or finish ('failed' — unknown node, degenerate view area,
     * animation interrupted by the user). See ScriptOutcome for who settles state
     * in each case.
     */
    async run(script: AnimationScript): Promise<ScriptOutcome> {
        this.cancel();
        const token = this.generation;
        for (const step of script) {
            const completed = await this.executeStep(step);
            if (token !== this.generation) return 'superseded';
            if (!completed) return 'failed';
        }
        return 'completed';
    }

    cancel(): void {
        this.generation += 1;
        this.cancelPendingWait?.();
        this.cancelPendingWait = null;
    }

    private executeStep(step: AnimationStep): Promise<boolean> {
        switch (step.kind) {
            case 'selectNode':
                this.stage.selectNode(step.nodeId);
                return Promise.resolve(true);
            case 'highlightNode':
                return this.stage.highlightNode(step.nodeId, step.color);
            case 'scrollGuideToSection':
                this.stage.scrollGuideToSection(step.sectionId);
                return Promise.resolve(true);
            case 'openSkillPanel':
                this.stage.setSkillPanelOpen(true);
                return Promise.resolve(true);
            case 'closeSkillPanel':
                this.stage.setSkillPanelOpen(false);
                return Promise.resolve(true);
            case 'wait':
                return this.wait(step.ms);
            case 'panToNode':
                return this.animateToNode(step.nodeId, this.stage.getCurrentZoom(), step);
            case 'zoomToNode':
                return this.animateToNode(step.nodeId, step.k, step);
            case 'zoomToPoint':
                return this.animateToFocus(step.x, step.y, step.k, step);
            case 'zoomToFit':
                return this.animateToFit(step);
        }
    }

    private wait(ms: number): Promise<boolean> {
        return new Promise(resolve => {
            const timer = setTimeout(() => {
                this.cancelPendingWait = null;
                resolve(true);
            }, ms);
            this.cancelPendingWait = () => {
                clearTimeout(timer);
                resolve(false);
            };
        });
    }

    private animateToNode(nodeId: string, k: number, options: ViewportStepOptions): Promise<boolean> {
        const center = this.stage.getNodeCenter(nodeId);
        if (!center) return Promise.resolve(false);
        return this.animateToFocus(center.x, center.y, k, options);
    }

    private animateToFocus(
        focusX: number,
        focusY: number,
        k: number,
        options: ViewportStepOptions,
    ): Promise<boolean> {
        const area = this.stage.getViewArea({ skillPanel: options.skillPanel });
        const target = computeCenteredViewport({
            focusX,
            focusY,
            k: this.clampZoom(k),
            availableWidth: area.availableWidth,
            windowHeight: area.windowHeight,
        });
        if (!target) return Promise.resolve(false);
        return this.stage.animateViewport(target, options.durationMs ?? DEFAULT_VIEW_ANIMATION_MS);
    }

    private animateToFit(step: { paddingPx?: number } & ViewportStepOptions): Promise<boolean> {
        const bounds = this.stage.getTreeBounds();
        if (!bounds) return Promise.resolve(false);
        const area = this.stage.getViewArea({ skillPanel: step.skillPanel });
        const limits = this.stage.getZoomLimits();
        const target = computeFitViewport({
            bounds,
            availableWidth: area.availableWidth,
            windowHeight: area.windowHeight,
            paddingPx: step.paddingPx,
            minK: limits.min,
            maxK: limits.max,
        });
        if (!target) return Promise.resolve(false);
        return this.stage.animateViewport(target, step.durationMs ?? DEFAULT_VIEW_ANIMATION_MS);
    }

    private clampZoom(k: number): number {
        const limits = this.stage.getZoomLimits();
        return Math.min(limits.max, Math.max(limits.min, k));
    }
}
